import h from 'hyperscript';
import icon from '../../../javascripts/utils/icon';

const WHITESPACES = /^\s+|\s+$/g;

const SELECTORS = {
  active: 'search-input--active',
  loading: 'search-input--loading',
  reset: 'search-input--reset',
  resetButton: '.search-input__reset',
  container: '.search-input__container',
  form: '.header__search-form',
  list: 'ul.search-input__list',
  listTitle: 'span.search-input__list-title',
  listItem: 'li.search-input__list-item',
  listIcon: 'search-input__list-icon',
  listResultItem: 'li.search-input__list-item.search-input__list-item--results',
};

class SearchInput {
  constructor($el) {
    this.$el = $el;
    this.$input = this.$el.querySelector('input');
    this.$form = this.$el.parentNode;
    this.$reset = this.$el.querySelector(SELECTORS.resetButton);
    this.dataSource = this.$el.dataset.typesearch;
    this.closable = this.$el.dataset.typesearchClosable;

    this.init();
  }

  init() {
    this.initInput();
    this.initEvents();
  }

  initInput() {
    if (this.inputIsPresent()) this.setReset();
    if (!this.$reset) return;
    this.$reset.addEventListener('click', (event) => {
      event.stopPropagation();

      this.reset();
    });
  }

  initEvents() {
    this.$input.addEventListener('click', (event) => {
      event.stopPropagation();
    });
    if (this.isExpanded()) {
      this.$container.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    }
    this.$input.addEventListener('input', () => {
      this.clearSuggestions();
      this.unsetReset();

      if (this.inputIsPresent()) {
        this.setActiveState();
        this.get(this.sourceUrl(), this.updateSuggestions.bind(this));
      } else {
        this.unsetActiveState();
      }
    });

    this.$input.addEventListener('keydown', (event) => {
      if (this.isExpanded()) {
        // Down
        if (event.keyCode === 40) {
          event.preventDefault();

          this.selectNextListItem();
        }

        // Up
        if (event.keyCode === 38) {
          event.preventDefault();

          this.selectPreviousListItem();
        }

        // Return
        if (event.keyCode === 13) {
          if (this.hasAriaSelectedItem()) {
            event.preventDefault();
            this.selectCurrentListItem();
          } else {
            this.$form.submit();
          }
        }

        // Escape
        if (event.keyCode === 27) {
          event.preventDefault();

          this.clearSuggestions();
        }
      }
    });
  }

  setActiveState() {
    this.$el.classList.add(SELECTORS.active);
  }

  unsetActiveState() {
    this.$el.classList.remove(SELECTORS.active);
  }

  setLoading() {
    this.$el.classList.add(SELECTORS.loading);
  }

  unsetLoading() {
    this.$el.classList.remove(SELECTORS.loading);
  }

  setReset() {
    this.$el.classList.add(SELECTORS.reset);
  }

  unsetReset() {
    this.$el.classList.remove(SELECTORS.reset);
  }

  selectPreviousListItem() {
    const $currentSelectedItem = this.hasAriaSelectedItem();

    if ($currentSelectedItem) {
      if ($currentSelectedItem.previousSibling) {
        $currentSelectedItem.previousSibling.setAttribute('aria-selected', 'true');
        $currentSelectedItem.setAttribute('aria-selected', 'false');

        this.$typeheadContainer.scrollTop = $currentSelectedItem.previousSibling.offsetTop -
          this.$typeheadContainer.offsetTop;
      }
    } else {
      const $el = this.$el.querySelector(`${SELECTORS.listItem}:last-child`);
      $el.setAttribute('aria-selected', 'true');
    }
  }

  selectNextListItem() {
    const $currentSelectedItem = this.hasAriaSelectedItem();

    if ($currentSelectedItem) {
      if ($currentSelectedItem.nextSibling) {
        $currentSelectedItem.nextSibling.setAttribute('aria-selected', 'true');
        $currentSelectedItem.setAttribute('aria-selected', 'false');

        this.$typeheadContainer.scrollTop = $currentSelectedItem.nextSibling.offsetTop -
          this.$typeheadContainer.offsetTop;
      }
    } else {
      const $el = this.$el.querySelector(`${SELECTORS.listItem}:first-child`);
      $el.setAttribute('aria-selected', 'true');
    }
  }

  selectCurrentListItem() {
    const $currentSelectedItem = this.hasAriaSelectedItem();

    if ($currentSelectedItem) $currentSelectedItem.click();
  }

  initListBindings() {
    this.$el.querySelectorAll(SELECTORS.listItem).forEach(($listItem) => {
      $listItem.addEventListener('click', (event) => {
        event.stopPropagation();

        const $currentTarget = event.currentTarget;

        if ($currentTarget.querySelector('a')) {
          $currentTarget.querySelector('a').click();
        } else {
          this.$input.value = $currentTarget.innerText;
        }

        this.clearSuggestions();
      });
    });
  }

  updateSuggestions(data) {
    const $containerElements = [];

    if (data.suggestions.length) {
      const suggestions = data.suggestions.map(
        value => h(SELECTORS.listItem, value),
      );

      if (data.results) {
        suggestions.push(
          h(SELECTORS.listResultItem,
            h('a', { href: data.results.url }, `Alle ${data.results.count} Ergebnisse anzeigen`),
            icon({
              icon: 'arrow-right',
              classes: [SELECTORS.listIcon],
            }),
          ),
        );
      }

      // $containerElements.push(h(SELECTORS.listTitle, 'Oder meinten Sie:'));
      $containerElements.push(h(SELECTORS.list, suggestions));

      if (this.closable) {
        $containerElements.push(
          h('.search-input__close',
            h('.search-input__title', 'Suche schließen'),
            icon({
              icon: 'close',
              classes: ['search-input__close-icon'],
            }),
          ),
        );
      }

      this.$typeheadContainer = h(SELECTORS.container, $containerElements);
      this.$el.appendChild(this.$typeheadContainer);

      this.initListBindings();
    }
  }

  clearSuggestions() {
    this.$typesearchContainer = this.$el.querySelector(SELECTORS.container);

    if (this.$typesearchContainer) this.$typesearchContainer.remove();
  }

  sourceUrl() {
    return this.dataSource.includes('.json') ? this.dataSource : `${this.dataSource}${this.$input.value}`;
  }

  isExpanded() {
    return this.$el.querySelector(SELECTORS.list);
  }

  hasAriaSelectedItem() {
    return this.$el.querySelector('[aria-selected="true"]');
  }

  inputIsPresent() {
    return this.$input.value.replace(WHITESPACES, '') !== '';
  }

  reset() {
    this.$input.value = '';
    this.unsetReset();
  }

  get(url, successCallback) {
    this.setLoading();
    this.req = new XMLHttpRequest();
    this.req.open('get', url);
    this.req.onload = () => {
      this.unsetLoading();

      if (this.req.status === 200) successCallback(JSON.parse(this.req.response));
      else Error(this.req.statusText);
    };

    this.req.send();
  }
}

document.querySelectorAll('[data-typesearch]').forEach(
  $el => new SearchInput($el),
);



// WEBPACK FOOTER //
// ./src/components/molecules/search-input/search-input.js